import type { DocumentTypeArchivedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeArchived';
import type { DocumentTypeCreatedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeCreated';
import type { DocumentTypeRenamedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeRenamed';
import type { DocumentTypeRestoredAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeRestored';
import type { DocumentTypesFetchedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypesFetched';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import DocumentTypeView from '@UI/Store/DocumentTypes/Views/DocumentTypeView';
import DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type DocumentTypesStore = {
	documentTypes: DocumentTypesListView | null;
};

const initialState: DocumentTypesStore = {
	documentTypes: null,
};

type Actions =
	| DocumentTypesFetchedAction
	| DocumentTypeCreatedAction
	| DocumentTypeRenamedAction
	| DocumentTypeArchivedAction
	| DocumentTypeRestoredAction
	| UserRevokedAction;

const whenDocumentTypesFetched = (
	store: DocumentTypesStore,
	action: DocumentTypesFetchedAction,
): DocumentTypesStore => ({
	...store,
	documentTypes: new DocumentTypesListView(
		action.accountId,
		action.payload.map((entry) => new DocumentTypeView(entry)),
	),
});

// Every mutation below ignores an event scoped to an account other than the one currently loaded:
// the catalogue this screen shows never mixes rows from two accounts.
const whenDocumentTypeCreated = (
	store: DocumentTypesStore,
	action: DocumentTypeCreatedAction,
): DocumentTypesStore => {
	if (store.documentTypes === null || store.documentTypes.accountId !== action.accountId) {
		return store;
	}

	return { ...store, documentTypes: store.documentTypes.add(new DocumentTypeView(action.payload)) };
};

const whenDocumentTypeRenamed = (
	store: DocumentTypesStore,
	action: DocumentTypeRenamedAction,
): DocumentTypesStore => {
	if (store.documentTypes === null || store.documentTypes.accountId !== action.accountId) {
		return store;
	}

	return { ...store, documentTypes: store.documentTypes.replace(new DocumentTypeView(action.payload)) };
};

const whenDocumentTypeArchived = (
	store: DocumentTypesStore,
	action: DocumentTypeArchivedAction,
): DocumentTypesStore => {
	if (store.documentTypes === null || store.documentTypes.accountId !== action.accountId) {
		return store;
	}

	return {
		...store,
		documentTypes: store.documentTypes.markArchived(
			action.payload.documentTypeId,
			action.payload.archivedAt,
		),
	};
};

const whenDocumentTypeRestored = (
	store: DocumentTypesStore,
	action: DocumentTypeRestoredAction,
): DocumentTypesStore => {
	if (store.documentTypes === null || store.documentTypes.accountId !== action.accountId) {
		return store;
	}

	return { ...store, documentTypes: store.documentTypes.markRestored(action.payload.documentTypeId) };
};

// A per-account catalogue has no reason to survive a session: the next session refetches it.
const whenUserRevoked = (): DocumentTypesStore => initialState;

export default registerStore<'documentTypesStore', Actions>(
	'documentTypesStore',
	createSliceReducer({
		DOCUMENT_TYPES_FETCHED: whenDocumentTypesFetched,
		DOCUMENT_TYPE_CREATED: whenDocumentTypeCreated,
		DOCUMENT_TYPE_RENAMED: whenDocumentTypeRenamed,
		DOCUMENT_TYPE_ARCHIVED: whenDocumentTypeArchived,
		DOCUMENT_TYPE_RESTORED: whenDocumentTypeRestored,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
